import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Description, Input, Label, Switch, TextArea, TextField } from '@heroui/react'
import { api } from '../lib/api'
import {
  branchesToSend,
  clockOf,
  describeSchedule,
  everyBranchFor,
  fitDraft,
  formatsFor,
  lastLetter,
  minuteOf,
  outsideDomains,
  parseRecipients,
  pickedOf,
  scheduleBanner,
  timeHint,
  viewsFor,
} from '../lib/reportSchedules'
import { dayName } from '../lib/clock'
import { explain } from '../lib/staffErrors'
import type { StaffKey } from '../lib/staffText'
import type { Branch, ReportFrequency, ReportSchedule, ReportView, ScheduleDraft, ScheduleFormat, ScheduleKind, ScheduleList } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { BranchChecks } from './BranchChecks'
import { Choice, Notice, Pick, ReportModal } from './kit'

/**
 * Reports by email: the schedules for the branches this person reads, and the
 * network's where they may compare branches.
 *
 * On the Reports screen rather than in Administration, because the person who
 * asks for the Monday letter is the person who reads the figures — a supervisor,
 * who holds report.read and no administration section. The mail server itself is
 * the owner's (Administration → Mail server); here it is only ever a banner.
 *
 * A row per letter, opening into the side panel to change it, as every list in
 * this design does. The letter carries the tables by person, so who it goes to is
 * the one thing on the panel that is said twice: in the field, and in the (i).
 */

interface Draft {
  id?: string
  branchId: string // a summary's: '' is the network
  /** A saved report's or Tickets letter's ticked branches. */
  picked: string[]
  kind: ScheduleKind
  viewId: string
  format: ScheduleFormat
  frequency: ReportFrequency
  time: string
  recipients: string
  enabled: boolean
}

const blank = (branchId: string, picked: string[]): Draft => ({ branchId, picked, kind: 'summary', viewId: '', format: 'csv', frequency: 'daily', time: '07:00', recipients: '', enabled: true })

const KINDS: ScheduleKind[] = ['summary', 'view', 'tickets']
const FORMAT_KEY: Record<ScheduleFormat, StaffKey> = { csv: 'reports.csv', xlsx: 'reports.excel', pdf: 'reports.pdf' }

export function Schedules({
  branches,
  branchId,
  onClose,
  toast,
}: {
  branches: Branch[]
  branchId: string
  onClose: () => void
  toast: (text: string) => void
}) {
  const { lang, say } = useStaffText()
  const [list, setList] = useState<ScheduleList | null>(null)
  const [views, setViews] = useState<ReportView[]>([])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)

  const load = useCallback(async () => {
    try {
      setList(await api.reportSchedules())
    } catch (err) {
      setError(explain(err, lang, 'reportMail.notLoaded'))
    }
  }, [lang])

  useEffect(() => {
    void load()
  }, [load])

  // The views of the organisation the open letter is for: the installation's owner
  // sees several, and unnamed the server could not tell which. None read is an
  // empty picker, not a fault of the schedules.
  const viewsOrg = draft
    ? draft.id
      ? list?.schedules.find((s) => s.id === draft.id)?.tenantId
      : branches.find((b) => b.id === (draft.branchId || draft.picked[0]))?.tenantId
    : undefined
  useEffect(() => {
    let live = true
    api.reportViews(viewsOrg).then(
      (r) => live && setViews(r.views),
      () => live && setViews([]),
    )
    return () => {
      live = false
    }
  }, [viewsOrg])

  // A branch this person does not read is not in the list; its rollover is then
  // unknown here, and the chosen minute is shown as it is.
  const rolloverOf = (id: string | undefined) => (id ? branches.find((b) => b.id === id)?.rolloverMinute ?? 0 : 0)
  const branchName = (s: Pick<ReportSchedule, 'branchId' | 'branchName' | 'branchNames' | 'kind'>) =>
    (s.branchNames?.length ?? 0) > 1
      ? s.branchNames!.join(', ')
      : s.branchId
      ? (s.branchName ?? branches.find((b) => b.id === s.branchId)?.name ?? s.branchId)
      : say(s.kind === 'tickets' ? 'reports.everyBranch' : 'reportMail.network')
  const sends = (s: ReportSchedule) => [say(`reportMail.kind.${s.kind}`), s.viewName, say(FORMAT_KEY[s.format])].filter(Boolean).join(' · ')

  // One modal at a time: the letter's editor takes the list's place while it is open.
  if (draft && list)
    return (
      <MailDrawer
        draft={draft}
        list={list}
        views={views}
        branches={branches}
        busy={busy}
        asking={asking}
        rolloverOf={rolloverOf}
        title={
          draft.id
            ? branchName({ ...list.schedules.find((s) => s.id === draft.id), branchId: draft.branchId || undefined, kind: draft.kind })
            : say('insight.mail.new')
        }
        onClose={() => {
          setDraft(null)
          setAsking(false)
        }}
        onChange={setDraft}
        onSave={async (body) => {
          setBusy(true)
          setError('')
          try {
            if (draft.id) await api.updateReportSchedule(draft.id, body)
            else await api.createReportSchedule(body)
            setDraft(null)
            toast(say('insight.mail.saved'))
            await load()
          } catch (err) {
            setError(explain(err, lang, 'common.unreachable'))
            setDraft(null)
          } finally {
            setBusy(false)
          }
        }}
        onSend={async () => {
          if (!draft.id) return
          setError('')
          try {
            const { periodFrom, periodTo } = await api.sendReportNow(draft.id)
            toast(say('insight.mail.queued', { period: periodFrom === periodTo ? dayName(periodFrom, lang) : `${dayName(periodFrom, lang)} – ${dayName(periodTo, lang)}` }))
            setDraft(null)
            await load()
          } catch (err) {
            setError(explain(err, lang, 'common.unreachable'))
            setDraft(null)
          }
        }}
        onAskDelete={() => setAsking(true)}
        onKeep={() => setAsking(false)}
        onDelete={async () => {
          if (!draft.id) return
          try {
            await api.deleteReportSchedule(draft.id)
            setDraft(null)
            setAsking(false)
            toast(say('insight.mail.stopped'))
            await load()
          } catch (err) {
            setError(explain(err, lang, 'common.unreachable'))
            setDraft(null)
            setAsking(false)
          }
        }}
      />
    )

  return (
    <ReportModal
      title={say('reportMail.title')}
      size="lg"
      onClose={onClose}
      footer={() => (
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('ui.close')}
          </Button>
          <Button variant="primary" size="sm" onPress={() => setDraft(blank(branchId, branches.map((b) => b.id)))}>
            <Icon name="plus" size={16} /> {say('reportMail.add')}
          </Button>
        </>
      )}
    >
      {error && <Notice>{error}</Notice>}

      {list && list.schedules.length === 0 && <p className="rp-mail-none">{say('reportMail.none')}</p>}

      {list && list.schedules.length > 0 && (
        <ul className="rp-mail-rows">
          {list.schedules.map((s) => {
            const banner = scheduleBanner(s)
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className="rp-mailrow"
                  data-off={!s.enabled || undefined}
                  onClick={() =>
                    setDraft({
                      id: s.id,
                      branchId: s.branchId ?? '',
                      picked: pickedOf(s, branches.map((b) => b.id)),
                      kind: s.kind,
                      viewId: s.viewId ?? '',
                      format: s.format,
                      frequency: s.frequency,
                      time: clockOf(s.sendMinute),
                      recipients: s.recipients.join(', '),
                      enabled: s.enabled,
                    })
                  }
                >
                  <span className="rp-mailrow__main">
                    <strong>{branchName(s)}</strong>
                    <span className="rp-mailrow__what">{sends(s)}</span>
                    <span className="rp-mailrow__when">{describeSchedule(s, lang, rolloverOf(s.branchId))}</span>
                    <span className="rp-mailrow__to">{s.recipients.join(', ')}</span>
                    {banner && (
                      <span className="rp-mailrow__warn" data-tone={banner.tone}>
                        {say(banner.key, banner.vars)}
                      </span>
                    )}
                  </span>
                  <span className="rp-mailrow__last">{lastLetter(s, lang)}</span>
                  {!s.enabled ? (
                    <Chip size="sm" variant="tertiary">
                      {say('reportMail.off')}
                    </Chip>
                  ) : s.problem ? (
                    // On, but nothing goes until the problem above is put right: never "On".
                    <Chip size="sm" variant="soft" color="warning">
                      {say('reportMail.notSending')}
                    </Chip>
                  ) : (
                    <Chip size="sm" variant="soft" color="success">
                      {say('reportMail.enabled')}
                    </Chip>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </ReportModal>
  )
}

function MailDrawer({
  draft,
  list,
  views,
  branches,
  busy,
  asking,
  rolloverOf,
  title,
  onClose,
  onChange,
  onSave,
  onSend,
  onAskDelete,
  onKeep,
  onDelete,
}: {
  draft: Draft
  list: ScheduleList
  views: ReportView[]
  branches: Branch[]
  busy: boolean
  asking: boolean
  rolloverOf: (id: string | undefined) => number
  title: string
  onClose: () => void
  onChange: (d: Draft) => void
  onSave: (body: ScheduleDraft) => void
  onSend: () => void
  onAskDelete: () => void
  onKeep: () => void
  onDelete: () => void
}) {
  const { lang, say } = useStaffText()
  const parsed = parseRecipients(draft.recipients)
  // A schedule being switched off is not held to the domains: stopping a letter
  // must always be possible, including after the owner narrowed the domains under
  // it. The server makes the same exception, and refuses anything more.
  const outside = draft.enabled ? outsideDomains(parsed.addresses, list.allowedDomains) : []
  const minute = minuteOf(draft.time)
  const problem = parsed.problem
    ? say(parsed.problem, parsed.vars)
    : outside.length
      ? say('reportMail.outside', { addresses: outside.join(', ') })
      : minute === null
        ? say('errors.VALIDATION_FAILED.SCHEDULE_TIME')
        : ''
  // Save stays shut while anything is wrong, but an empty To field on a form just
  // opened is not yet a mistake, so it is not shown as one.
  const showProblem = problem && !(parsed.problem === 'errors.VALIDATION_FAILED.RECIPIENTS_MISSING' && !draft.recipients.trim())
  const listed = viewsFor(draft.kind, views)
  const noView = draft.kind !== 'summary' && !draft.viewId
  const noBranch = draft.kind !== 'summary' && draft.picked.length === 0
  // The branch whose clock the letter keeps: a summary's, or the one ticked.
  const oneBranch = draft.kind === 'summary' ? draft.branchId : draft.picked.length === 1 ? draft.picked[0]! : ''
  const everyBranch = everyBranchFor(draft.kind, list.canNetwork, list.canAllBranches)
  // A schedule's branch is fixed: one for every branch keeps to the kinds that go there.
  const kinds = KINDS
  // Every branch is kept only where this kind may go there; a new letter otherwise starts at the first branch.
  const change = (d: Draft) => {
    const placed = !d.id && !d.branchId && !everyBranchFor(d.kind, list.canNetwork, list.canAllBranches) ? { ...d, branchId: branches[0]?.id ?? '' } : d
    onChange(fitDraft(placed, views))
  }
  const view = views.find((v) => v.id === draft.viewId)
  // Who it names: the summary's and the tickets' files do; a view only with Person placed.
  const timeSaid = timeHint(!!oneBranch, minute, rolloverOf(oneBranch || undefined), lang)
  const byPerson = draft.kind !== 'view' || (!!view?.spec.layout && [...view.spec.layout.rows, ...view.spec.layout.cols].includes('person')) || (!!view?.spec.pivot && [...view.spec.pivot.rows, ...view.spec.pivot.cols].includes('staff'))

  const extra = draft.id ? (
    asking ? (
      <>
        <strong className="rp-modal__ask">{say('insight.mail.stopAsk')}</strong>
        <Button variant="tertiary" size="sm" onPress={onKeep}>
          {say('reportMail.cancel')}
        </Button>
        <Button variant="danger" size="sm" onPress={onDelete}>
          {say('reportMail.delete')}
        </Button>
      </>
    ) : (
      <>
        <Button variant="danger-soft" size="sm" onPress={onAskDelete}>
          {say('reportMail.delete')}
        </Button>
        <Button variant="tertiary" size="sm" onPress={onSend}>
          {say('reportMail.sendNow')}
        </Button>
      </>
    )
  ) : undefined
  const valid = !problem && minute !== null && !noView && !noBranch

  return (
    <ReportModal
      title={title}
      askBeforeClose
      onClose={onClose}
      extra={extra}
      onSubmit={() => {
        if (!valid || busy) return
        onSave({
          ...(draft.kind === 'summary'
            ? { branchId: draft.branchId || undefined }
            : { branchIds: branchesToSend(draft.picked, branches.map((b) => b.id), everyBranch) }),
          kind: draft.kind,
          viewId: draft.kind === 'summary' ? undefined : draft.viewId,
          format: draft.format,
          frequency: draft.frequency,
          sendMinute: minute!,
          recipients: parsed.addresses,
          enabled: draft.enabled,
        })
      }}
      footer={(form) => (
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" form={form} variant="primary" size="sm" isDisabled={!valid || busy}>
            {say('reportMail.save')}
          </Button>
        </>
      )}
    >
      <div className="rp-field">
        <span className="rp-field__label">{say('reportMail.what')}</span>
        <Choice label={say('reportMail.what')} value={draft.kind} onChange={(k) => change({ ...draft, kind: k })} options={kinds.map((k) => ({ value: k, label: say(`reportMail.kind.${k}`) }))} />
      </div>
      {draft.kind !== 'summary' && (
        <div className="rp-field">
          <span className="rp-field__label" id="insight-mail-branches">
            {say('pivot.branches')}
          </span>
          <BranchChecks branches={branches} picked={draft.picked} onChange={(picked) => onChange({ ...draft, picked })} labelledBy="insight-mail-branches" />
        </div>
      )}
      {!draft.id && draft.kind === 'summary' && (
        <div className="rp-field">
          <span className="rp-field__label">{say('common.branch')}</span>
          <Pick
            label={say('common.branch')}
            value={draft.branchId}
            onChange={(v) => change({ ...draft, branchId: v })}
            options={[...branches.map((b) => ({ value: b.id, label: b.name })), ...(everyBranch ? [{ value: '', label: say('reportMail.network') }] : [])]}
          />
        </div>
      )}
      {draft.kind !== 'summary' && (
        <div className="rp-field">
          <span className="rp-field__label">{say('reportMail.view')}</span>
          {listed.length > 0 ? (
            <Pick
              label={say('reportMail.view')}
              value={draft.viewId}
              onChange={(v) => change({ ...draft, viewId: v })}
              options={listed.map((v) => ({ value: v.id, label: v.name, note: v.mine ? undefined : v.ownerName }))}
            />
          ) : (
            <p className="rp-muted">{say('reportMail.noViews')}</p>
          )}
        </div>
      )}
      <div className="rp-field">
        <span className="rp-field__label">{say('reportMail.file')}</span>
        <Choice horizontal label={say('reportMail.file')} value={draft.format} onChange={(f) => change({ ...draft, format: f })} options={formatsFor(draft.kind, draft.branchId).map((f) => ({ value: f, label: say(FORMAT_KEY[f]) }))} />
      </div>
      <div className="rp-field">
        <span className="rp-field__label">{say('reportMail.frequency')}</span>
        <Choice
          label={say('reportMail.frequency')}
          value={draft.frequency}
          onChange={(f) => onChange({ ...draft, frequency: f })}
          options={(['daily', 'weekly', 'monthly'] as const).map((f) => ({ value: f, label: say(`reportMail.${f}`), tip: say(`insight.mail.covers.${f}`) }))}
        />
      </div>
      <TextField className="rp-field--narrow" value={draft.time} onChange={(time) => onChange({ ...draft, time })}>
        <Label title={timeSaid}>{say('reportMail.time')}</Label>
        <Input type="time" />
        {/* Said only when the branch's late day-end moves the letter. */}
        {timeSaid !== timeHint(!!oneBranch, null, rolloverOf(oneBranch || undefined), lang) && <Description>{timeSaid}</Description>}
      </TextField>
      <TextField value={draft.recipients} onChange={(recipients) => onChange({ ...draft, recipients })} isInvalid={!!showProblem}>
        <Label title={say('reportMail.recipientsHint') + (byPerson ? ' ' + say('insight.mail.who') : '')}>{say('reportMail.recipients')}</Label>
        <TextArea rows={3} />
        {list.allowedDomains.length > 0 && <Description>{say('reportMail.domains', { domains: list.allowedDomains.join(', ') })}</Description>}
      </TextField>
      {showProblem && (
        <p className="rp-problem" role="alert">
          {problem}
        </p>
      )}
      <Switch className="rp-switch" isSelected={draft.enabled} onChange={(enabled) => onChange({ ...draft, enabled })}>
        <Switch.Content className="rp-switch__row">
          <Label>{say('insight.mail.sending')}</Label>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Content>
      </Switch>
    </ReportModal>
  )
}
